(ns app.bulk.actions
  "Replicant action handlers for the bulk scheduling slice."
  (:require [app.bulk.gallery-modal :as gallery-modal]
            [app.bulk.page :as bulk-page]
            [parts.replicant.query :as query]))

(def register
  [{:ui.action/kind :bulk/maybe-discard
    :ui.action/handler
    (fn [{:keys [action store ui/event-handler]}]
      (let [[_ on-confirm] action]
        (if (bulk-page/unsaved-changes-in-state? @store)
          (event-handler {} [[:ui/modal-open
                              :modals/bulk-unsaved-confirm
                              {:on-confirm on-confirm}]])
          (event-handler {} on-confirm))))}

   {:ui.action/kind :bulk/discard-and-navigate
    :ui.action/handler
    (fn [{:keys [action store]}]
      (let [[_ url] action]
        (swap! store
               (fn [s]
                 (-> s
                     (dissoc :app.bulk.page/rows
                             :app.bulk.page/loaded-rows
                             :app.bulk.page/awaiting-refresh?)
                     (bulk-page/clear-selection))))
        (set! (.-href js/location) url)))}

   {:ui.action/kind :bulk/save-succeeded
    :ui.action/handler
    ;; Keeps the just-saved rows on screen and marks the page as
    ;; awaiting the refreshed query result. Clearing the rows before
    ;; the refresh lands would render the stale pre-save query result,
    ;; so saved values (e.g. a highlight selection) would flash back to
    ;; their old state after every save. `:bulk/finish-refresh` drops
    ;; the kept rows once the refresh has landed.
    (fn [{:keys [store ui/event-handler]}]
      (swap! store
             (fn [s]
               (-> s
                   (dissoc :app.bulk.page/saving?)
                   (assoc :app.bulk.page/save-status :success
                          :app.bulk.page/awaiting-refresh? true
                          :app.bulk.page/loaded-rows (:app.bulk.page/rows s)))))
      (event-handler {} [[:data/query (bulk-page/bulk-query @store)]]))}

   {:ui.action/kind :bulk/finish-refresh
    :ui.action/handler
    (fn [{:keys [store]}]
      (let [state @store]
        (when (and (:app.bulk.page/awaiting-refresh? state)
                   (not (query/loading? state (bulk-page/bulk-query state))))
          ;; the refresh replaces the saved rows with re-created posts
          ;; under new eids, so the row selection must not survive it:
          (swap! store
                 (fn [s]
                   (-> s
                       (dissoc :app.bulk.page/rows
                               :app.bulk.page/loaded-rows
                               :app.bulk.page/awaiting-refresh?)
                       (bulk-page/clear-selection)))))))}

   {:ui.action/kind :bulk/row-select-click
    :ui.action/handler
    (fn [{:keys [store action] :as w}]
      (let [[_ eid] action
            shift? (boolean (some-> (:replicant/js-event w)
                                    (.-shiftKey)))]
        (swap! store bulk-page/row-select-click eid shift?)))}

   {:ui.action/kind :bulk/fb-click
    :ui.action/handler
    (fn [{:keys [store action] :as w}]
      (let [[_ eid default-linkage] action
            shift? (boolean (some-> (:replicant/js-event w)
                                    (.-shiftKey)))]
        (swap! store bulk-page/fb-click eid default-linkage shift?)))}

   {:ui.action/kind :bulk/ai-click
    :ui.action/handler
    (fn [{:keys [store action] :as w}]
      (let [[_ eid] action
            shift? (boolean (some-> (:replicant/js-event w)
                                    (.-shiftKey)))]
        (swap! store bulk-page/ai-click eid shift?)))}

   {:ui.action/kind :bulk/capture-slot-tops
    :ui.action/handler
    ;; snapshots the pixel position of every data row (the group
    ;; separator rows excluded) at dragstart; the drag preview
    ;; translates the rows between these static slots, see
    ;; `bulk-page/row-shift-px`. offsetTop is a layout metric that
    ;; the preview transforms do not affect.
    (fn [{:keys [store]}]
      (when-let [tbody (js/document.querySelector "#bulk-schedule-table tbody")]
        (let [tops (->> (array-seq (.-children tbody))
                        (remove #(.getAttribute % "data-separator"))
                        (mapv #(.-offsetTop %)))]
          (swap! store assoc :app.bulk.page/slot-tops tops))))}

   {:ui.action/kind :bulk-gallery/item-click
    :ui.action/handler gallery-modal/item-click}

   {:ui.action/kind :bulk-gallery/deselect-item
    :ui.action/handler
    (fn [{:keys [store action]}]
      (let [[_ eid] action]
        (swap! store
               (fn [s]
                 (-> s
                     (update :app.bulk.gallery-modal/selected-eids disj eid)
                     (update :app.bulk.gallery-modal/selected-entries
                             (fn [es]
                               (vec (remove #(= (:story-media/eid %) eid) es)))))))))}

   {:ui.action/kind :bulk/add-selected-to-schedule
    :ui.action/handler
    (fn [{:keys [store action]}]
      (let [[_ {:keys [entries date-str]}] action
            state (bulk-page/start-editing @store)
            ig-user-id (:app.bulk.page/selected-ig-user-id state)
            existing-rows (:app.bulk.page/rows state)
            date (js/Date. date-str)
            new-rows (vec (map-indexed
                            (fn [idx entry]
                              (let [post-date (js/Date. (.getTime date))]
                                (.setMinutes post-date
                                             (+ (.getMinutes post-date) idx))
                                {:story.post/eid (str "new-" (random-uuid))
                                 :story.post/media (:story-media/eid entry)
                                 :story.media/name (:story-media/name entry)
                                 :story.media/preview-url (:story-media/preview-url entry)
                                 :story.post/date-str (bulk-page/format-row-date-str post-date)
                                 :story.post/link (or (not-empty (:story-media/link entry)) "")
                                 :instagram/user-id ig-user-id
                                 :share-to-fb? false
                                 :story.post/ai-generated? false}))
                            entries))]
        (swap! store
               (fn [s]
                 (-> (bulk-page/start-editing s)
                     (assoc :app.bulk.page/rows
                            ;; keeps the stored ascending date order,
                            ;; so added stories appear at their slot
                            ;; instead of below the table:
                            (bulk-page/rows-in-date-order
                              (into (vec existing-rows) new-rows)))
                     (dissoc :app.bulk.page/save-status))))))}])
